/**
 * usePolling — repeatedly call a fetcher until a stop predicate is met
 * (or the component unmounts). Used for Plan progress: poll every 1s
 * while plan.status is pending|running.
 */

import { useEffect, useRef, useState } from "react";


export interface UsePollingOptions {
  /** Interval between polls in ms. */
  intervalMs?: number;
  /** Hard cap on total duration (default: 60s). */
  timeoutMs?: number;
}


export interface PollingState<T> {
  data:    T | null;
  error:   Error | null;
  loading: boolean;
  /** True if the poll loop has stopped (success, error, timeout, or unmount). */
  stopped: boolean;
}


/**
 * Run `fetcher` every `intervalMs` until `isDone(result)` returns true.
 *
 * Re-mounts the loop when `key` changes (e.g. when a new task is started
 * and we want to poll a different correlation_id).
 */
export function usePolling<T>(
  key: string | null,
  fetcher: () => Promise<T>,
  isDone: (latest: T) => boolean,
  options: UsePollingOptions = {},
): PollingState<T> {
  const intervalMs = options.intervalMs ?? 1000;
  const timeoutMs  = options.timeoutMs  ?? 60_000;

  const [data, setData]       = useState<T | null>(null);
  const [error, setError]     = useState<Error | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [stopped, setStopped] = useState<boolean>(true);

  // Avoid stale closures: keep the latest fetcher / predicate in refs.
  const fetcherRef = useRef(fetcher);
  const isDoneRef  = useRef(isDone);
  fetcherRef.current = fetcher;
  isDoneRef.current  = isDone;

  useEffect(() => {
    if (!key) {
      setData(null);
      setError(null);
      setLoading(false);
      setStopped(true);
      return;
    }

    let cancelled = false;
    const startedAt = Date.now();
    setLoading(true);
    setStopped(false);
    setError(null);

    let timer: ReturnType<typeof setTimeout> | null = null;

    const tick = async (): Promise<void> => {
      if (cancelled) return;
      try {
        const result = await fetcherRef.current();
        if (cancelled) return;
        setData(result);

        if (isDoneRef.current(result)) {
          setLoading(false);
          setStopped(true);
          return;
        }
        if (Date.now() - startedAt > timeoutMs) {
          setError(new Error(`polling timeout after ${timeoutMs}ms`));
          setLoading(false);
          setStopped(true);
          return;
        }
        timer = setTimeout(tick, intervalMs);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e : new Error(String(e)));
        setLoading(false);
        setStopped(true);
      }
    };

    void tick();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, intervalMs, timeoutMs]);

  return { data, error, loading, stopped };
}
